import { FC } from 'react';

import { Box, Text, useToken, VStack } from '@bonusly/design-system';

import { RichTextDisplay } from '../../components/rich-text-editor/components/rich-text-display';
import { sanitize } from '../../hooks/use-sanitize-rich-text';
import { EmployeeSpotlightSection } from '../../schemas/spotlight';

interface SpotlightCardProps {
	section: EmployeeSpotlightSection;
	isActive: boolean;
}

const formatTypeAsBadge = (type: string): string => {
	return type
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

const processQuoteParagraphs = (html: string): string => {
	// Convert <p>> "..."</p> patterns to <blockquote>...</blockquote>
	return html.replace(/<p>\s*>\s*"([^"]+)"([^<]*)<\/p>/g, (match, quoteText, afterQuote) => {
		const cleanText = quoteText.trim();
		const afterText = afterQuote.trim();
		const fullText = afterText ? `${cleanText}${afterText}` : cleanText;
		return `<blockquote>${fullText}</blockquote>`;
	});
};

export const SpotlightCard: FC<SpotlightCardProps> = ({ section, isActive }) => {
	const whiteAlpha500 = useToken('colors', 'whiteAlpha.500');

	if (!isActive) {
		return null;
	}
	const processedHtml = processQuoteParagraphs(section.textHtml);
	const sanitizedHtml = sanitize(processedHtml);
	const introText = section.intro?.replace(/\.$/, '') || '';

	return (
		<Box
			position="absolute"
			top={0}
			left={0}
			right={0}
			opacity={isActive ? 1 : 0}
			visibility={isActive ? 'visible' : 'hidden'}
			pointerEvents={isActive ? 'auto' : 'none'}
			transition="opacity 0.3s ease, visibility 0.3s ease"
		>
			<VStack spacing={6} align="stretch">
				<Box textAlign="center">
					<Box
						as="span"
						display="inline-block"
						px={4}
						py={2}
						borderRadius="full"
						fontSize="xs"
						fontWeight="semibold"
						textTransform="uppercase"
						letterSpacing="wide"
						mb={4}
						bg="teal.100"
						color="text.default"
					>
						{formatTypeAsBadge(section.type)}
					</Box>
				</Box>

				<Box
					borderRadius="3xl"
					border="1.5px solid"
					borderColor="white"
					bg="whiteAlpha.700"
					boxShadow={`0 5.5px 30px 0 ${whiteAlpha500}`}
					backdropFilter="blur(9px)"
					p="15px"
					sx={{
						WebkitBackdropFilter: 'blur(9px)',
					}}
				>
					<Box bg="bg.surface" borderRadius="2xl" p={{ base: 8, md: '60px' }} display="flex" flexDirection="column" justifyContent="center">
						<VStack spacing={8} align="stretch">
							<VStack spacing={3} align="stretch">
								<Text
									fontFamily="'Plus Jakarta Sans', sans-serif"
									fontSize={{ base: 'md', md: 'lg' }}
									fontWeight="medium"
									lineHeight="shorter"
									color="text.default"
									opacity={0.7}
									textTransform="uppercase"
									letterSpacing="wide"
								>
									{introText}
								</Text>
								<Text
									as="h2"
									fontFamily="'Plus Jakarta Sans', sans-serif"
									fontSize={{ base: '2xl', md: '4xl' }}
									fontWeight="bold"
									lineHeight="shorter"
									color="text.default"
									mt={0}
								>
									{section.title}
								</Text>
							</VStack>

							<Box
								fontSize={{ base: 'md', md: 'xl' }}
								lineHeight="tall"
								color="text.default"
								sx={{
									'& p': {
										mb: 4,
									},
									'& blockquote': {
										position: 'relative',
										p: '16px 20px',
										m: ' 10px 0',
										borderLeft: 'none',
										borderRadius: '12px',
										bg: '#E6FCFA',
										fontSize: { base: 'md', md: 'xl' },
										lineHeight: 'tall',
										color: 'text.default',
										'& p': {
											mb: 0,
										},
									},
								}}
							>
								<RichTextDisplay>{sanitizedHtml}</RichTextDisplay>
							</Box>
						</VStack>
					</Box>
				</Box>
			</VStack>
		</Box>
	);
};
